import { Button, Col, Collapse, Row, Space, Tooltip, Typography } from 'antd'
import { useEffect, useState } from 'react'
import {
  BasicPermissionItem,
  CIMSModuleItem,
  CIMSPageItem,
  Permission,
  alphabeticalSort,
} from 'shared'
import CollapsePanel from 'antd/lib/collapse/CollapsePanel'
import { CIMSTag } from 'components/CIMSTag'
import { HelpMessage } from 'utils/constants'
import CIMSBadge from 'components/CIMSBadge'
import CIMSCheckbox from 'components/CIMSCheckbox'
import { PermissionEditorProps } from './types'
import CIMSSection from 'components/CIMSSection'

const PermissionEditor: React.FC<PermissionEditorProps> = ({
  roles,
  loading,
  help,
  onChange,
  value,
  disabled = false,
}) => {
  const setOpenPanels = (key: string | string[]) =>
    _setOpenPanels(Array.isArray(key) ? key : [key])
  const [openPanels, _setOpenPanels] = useState<string[]>([])
  const [rolePermissions, setRolePermissions] = useState<string[]>([
    ...new Set(roles?.flatMap(({ permissions }) => permissions)),
  ])
  const [checked, _setChecked] = useState<string[]>([
    ...new Set([...(value ?? []), ...rolePermissions]),
  ])
  useEffect(() => {
    _setChecked((prevChecked) => {
      const newChecked = prevChecked.filter(
        (permissionId) => !rolePermissions.includes(permissionId)
      )
      setRolePermissions([
        ...new Set(roles?.flatMap(({ permissions }) => permissions)),
      ])
      return newChecked
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roles])
  useEffect(() => {
    _setChecked((prevChecked) => [
      ...new Set([...prevChecked, ...rolePermissions]),
    ])
  }, [rolePermissions])
  const allPermissionsMap = Permission.moduleToPageToBasics
  const isChecked = (permission: BasicPermissionItem) =>
    checked.includes(permission.id)
  const isRolePermission = (permission: BasicPermissionItem) =>
    rolePermissions.includes(permission.id)
  const isAddPermission = (permission: BasicPermissionItem) =>
    checked.includes(permission.id) && !rolePermissions.includes(permission.id)
  const setChecked = (
    state: boolean,
    permissionItems: BasicPermissionItem[]
  ) => {
    if (state) {
      _setChecked((prev) => [
        ...prev,
        ...permissionItems
          .filter((permissionItem) => !isChecked(permissionItem))
          .map(({ id }) => id),
      ])
    } else {
      _setChecked((prev) =>
        prev.filter(
          (id) =>
            rolePermissions.includes(id) ||
            !permissionItems.some((item) => item.id === id)
        )
      )
    }
  }
  const isIndeterminate = (item: CIMSModuleItem | CIMSPageItem) => {
    const permissions = Permission.findByModuleOrPage(item)
    const every = permissions.every(isChecked)
    const some = permissions.some(isChecked)
    return !every && some
  }
  const onHeaderChange = (item: CIMSModuleItem | CIMSPageItem) => {
    const permissions = Permission.findByModuleOrPage(item)
    const indeterminate = isIndeterminate(item)
    const none = permissions.every(isChecked)
    setChecked(indeterminate || !none, permissions)
  }
  const getCounts = (item: CIMSModuleItem | CIMSPageItem) => {
    const permissions = Permission.findByModuleOrPage(item)
    return {
      roleCount: permissions.filter((permission) =>
        isRolePermission(permission)
      ).length,
      addCount: permissions.filter((permission) => isAddPermission(permission))
        .length,
    }
  }
  useEffect(() => {
    onChange?.(checked)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checked])
  return (
    <CIMSSection help={help} title="Permissions">
      <Row justify="space-between" align="bottom" style={{ marginBottom: 10 }}>
        <Col>
          {roles ? (
            <Tooltip
              placement="right"
              title={
                checked.filter(
                  (permissionId) => !rolePermissions.includes(permissionId)
                ).length
                  ? HelpMessage.Administration.Components.ClearPermissions
                  : HelpMessage.Administration.Components
                      .ClearPermissionsDisabled
              }
            >
              <Button
                disabled={
                  !checked.filter(
                    (permissionId) => !rolePermissions.includes(permissionId)
                  ).length || disabled
                }
                onClick={() =>
                  _setChecked((prevChecked) =>
                    prevChecked.filter((permissionId) =>
                      rolePermissions.includes(permissionId)
                    )
                  )
                }
              >
                Clear Additional Permissions
              </Button>
            </Tooltip>
          ) : (
            <Button
              disabled={!checked.length || disabled}
              onClick={() => _setChecked([])}
            >
              Clear Permissions
            </Button>
          )}
        </Col>
        <Col>
          {!!openPanels.length && (
            <Button disabled={disabled} onClick={() => setOpenPanels([])}>
              Collapse All
            </Button>
          )}
        </Col>
      </Row>
      <Collapse
        /**
         * replace the nested Collapses with nested CIMSCollapses
         *
         * @expires 2024-06-01
         */
        style={{ width: 'auto', minWidth: 400 }}
        activeKey={openPanels}
        onChange={setOpenPanels}
        collapsible={disabled ? 'disabled' : undefined}
      >
        {[...allPermissionsMap.keys()]
          .sort(alphabeticalSort('name'))
          .map((moduleItem) => {
            const { roleCount, addCount } = getCounts(moduleItem)
            const extra = roleCount ? (
              addCount ? (
                <>
                  <CIMSBadge count={roleCount} backgroundColor="grey">
                    Role Permissions +&nbsp;
                  </CIMSBadge>
                  <CIMSBadge count={addCount}>Additional</CIMSBadge>
                </>
              ) : (
                <CIMSBadge count={roleCount} backgroundColor="grey">
                  Role Permissions
                </CIMSBadge>
              )
            ) : addCount ? (
              <CIMSBadge count={addCount}>Additional</CIMSBadge>
            ) : (
              ''
            )
            return (
              <CollapsePanel
                key={moduleItem.id}
                extra={roles ? extra : <CIMSBadge count={addCount} />}
                header={
                  <Space size="small" align="center">
                    <CIMSCheckbox
                      disabled={[
                        { disabled: loading ?? false, reason: 'loading' },
                        {
                          disabled:
                            Permission.findByModule(moduleItem).every(
                              isRolePermission
                            ),
                          reason: {
                            why: 'disabled',
                            message:
                              'All permissions in this Module are assigned by Role',
                          },
                        },
                        {
                          disabled,
                          reason: {
                            why: 'disabled',
                            message: 'Permission Editor is disabled',
                          },
                        },
                      ]}
                      indeterminate={isIndeterminate(moduleItem)}
                      checked={Permission.findByModule(moduleItem).every(
                        isChecked
                      )}
                      onChange={() => onHeaderChange(moduleItem)}
                    />
                    <Typography.Text>{moduleItem.name}</Typography.Text>
                  </Space>
                }
              >
                <Collapse activeKey={openPanels} onChange={setOpenPanels}>
                  {[...allPermissionsMap.get(moduleItem)!.keys()]
                    .sort(alphabeticalSort('name'))
                    .map((pageItem) => {
                      const { roleCount, addCount } = getCounts(pageItem)
                      const extra = roleCount ? (
                        addCount ? (
                          <Space size={'small'}>
                            <CIMSBadge count={roleCount} backgroundColor="grey">
                              +
                            </CIMSBadge>
                            <CIMSBadge count={addCount} />
                          </Space>
                        ) : (
                          <CIMSBadge count={roleCount} backgroundColor="grey" />
                        )
                      ) : addCount ? (
                        <CIMSBadge count={addCount} />
                      ) : (
                        ''
                      )
                      return (
                        <CollapsePanel
                          key={pageItem.id}
                          extra={roles ? extra : <CIMSBadge count={addCount} />}
                          header={
                            <Space size="small" align="center">
                              <CIMSCheckbox
                                disabled={[
                                  {
                                    disabled: loading ?? false,
                                    reason: 'loading',
                                  },
                                  {
                                    disabled:
                                      Permission.findByPage(pageItem).every(
                                        isRolePermission
                                      ),
                                    reason: {
                                      why: 'disabled',
                                      message:
                                        'All permissions in this Page are assigned by Role',
                                    },
                                  },
                                ]}
                                indeterminate={isIndeterminate(pageItem)}
                                checked={Permission.findByPage(pageItem).every(
                                  isChecked
                                )}
                                onChange={() => onHeaderChange(pageItem)}
                              />
                              <Typography.Text>{pageItem.name}</Typography.Text>
                            </Space>
                          }
                        >
                          <Space
                            size={'middle'}
                            direction="vertical"
                            style={{ margin: '10px 15px' }}
                          >
                            {[
                              ...allPermissionsMap
                                .get(moduleItem)!
                                .get(pageItem)!,
                            ]
                              .sort(alphabeticalSort('name'))
                              .map((permissionItem, i) => {
                                const roleNames = roles
                                  ?.filter(({ permissions }) =>
                                    permissions.includes(permissionItem.id)
                                  )
                                  .map(({ name }) => ` ${name}`)
                                return (
                                  <Space size="small" align="center">
                                    <CIMSCheckbox
                                      key={`${permissionItem.id}-${i}`}
                                      checked={checked.includes(
                                        permissionItem.id
                                      )}
                                      tooltipPlacement={'left'}
                                      disabled={[
                                        {
                                          disabled: loading ?? false,
                                          reason: 'loading',
                                        },
                                        {
                                          disabled: rolePermissions.includes(
                                            permissionItem.id
                                          ),
                                          reason: {
                                            why: 'disabled',
                                            message: `Assigned by Role${
                                              (roleNames?.length ?? 0) > 1
                                                ? 's'
                                                : ''
                                            }: ${roleNames}`,
                                          },
                                        },
                                      ]}
                                      onChange={() =>
                                        setChecked(!isChecked(permissionItem), [
                                          permissionItem,
                                        ])
                                      }
                                    />
                                    <Typography.Text>
                                      <CIMSTag>{permissionItem.name}</CIMSTag>
                                      {permissionItem.description}
                                    </Typography.Text>
                                  </Space>
                                )
                              })}
                          </Space>
                        </CollapsePanel>
                      )
                    })}
                </Collapse>
              </CollapsePanel>
            )
          })}
      </Collapse>
    </CIMSSection>
  )
}

export default PermissionEditor
